'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { reportsService, type ReportExport } from '@/lib/services/staffingService';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar,  } from 'recharts';

interface TimesheetRow {
  staffName: string;
  role: string;
  specialty: string;
  facility: string;
  shiftDate: string;
  regularHours: number;
  otHours: number;
  approvalStatus: string;
}

interface WeeklyPoint {
  week: string;
  totalHours: number;
  otHours: number;
  staffCount: number;
  utilizationPct: number;
}

interface FacilityPoint {
  facility: string;
  totalHours: number;
  staffCount: number;
}

const PERIOD_OPTIONS = [
  { label: 'Last 30 days', start: offsetDate(-30), end: offsetDate(0) },
  { label: 'Last 60 days', start: offsetDate(-60), end: offsetDate(0) },
  { label: 'Last 90 days', start: offsetDate(-90), end: offsetDate(0) },
];

function offsetDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function getWeekLabel(dateStr: string): string {
  const d = new Date(dateStr);
  const weekStart = new Date(d);
  weekStart.setDate(d.getDate() - d.getDay());
  return weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface Props {
  onExportLogged: (entry: ReportExport) => void;
}

export default function UtilizationTrends({ onExportLogged }: Props) {
  const [rows, setRows] = useState<TimesheetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodIdx, setPeriodIdx] = useState(0);
  const [exporting, setExporting] = useState(false);

  const period = PERIOD_OPTIONS[periodIdx];

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportsService.getTimesheetSummary(period.start, period.end);
      setRows(data as TimesheetRow[]);
    } catch (e: unknown) {
      const err = e as { message?: string };
      setError(err?.message || 'Failed to load utilization data');
    } finally {
      setLoading(false);
    }
  }, [period.start, period.end]);

  useEffect(() => { load(); }, [load]);

  // Weekly aggregation
  const weeklyMap: Record<string, WeeklyPoint> = {};
  rows.forEach((r) => {
    const wk = getWeekLabel(r.shiftDate);
    if (!weeklyMap[wk]) weeklyMap[wk] = { week: wk, totalHours: 0, otHours: 0, staffCount: 0, utilizationPct: 0 };
    weeklyMap[wk].totalHours += r.regularHours + r.otHours;
    weeklyMap[wk].otHours += r.otHours;
    weeklyMap[wk].staffCount += 1;
  });
  const weeklyData: WeeklyPoint[] = Object.values(weeklyMap).map((w) => ({
    ...w,
    utilizationPct: Math.min(100, Math.round((w.totalHours / (w.staffCount * 40)) * 100)),
  }));

  // Facility breakdown
  const facilityMap: Record<string, FacilityPoint> = {};
  rows.forEach((r) => {
    if (!facilityMap[r.facility]) facilityMap[r.facility] = { facility: r.facility, totalHours: 0, staffCount: 0 };
    facilityMap[r.facility].totalHours += r.regularHours + r.otHours;
    facilityMap[r.facility].staffCount += 1;
  });
  const facilityData = Object.values(facilityMap);

  const totalHours = rows.reduce((s, r) => s + r.regularHours + r.otHours, 0);
  const totalOT = rows.reduce((s, r) => s + r.otHours, 0);
  const otPct = totalHours > 0 ? ((totalOT / totalHours) * 100).toFixed(1) : '0';
  const uniqueStaff = new Set(rows.map((r) => r.staffName)).size;
  const avgHoursPerStaff = uniqueStaff > 0 ? (totalHours / uniqueStaff).toFixed(1) : '0';

  const handleExport = async () => {
    setExporting(true);
    try {
      const csv = [
        ['Week', 'Total Hours', 'OT Hours', 'Staff Count', 'Utilization %'].join(','),
        ...weeklyData.map((w) => [w.week, w.totalHours.toFixed(1), w.otHours.toFixed(1), w.staffCount, w.utilizationPct].join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = `utilization_trends_${period.start}_${period.end}.csv`;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);

      const logged = await reportsService.logExport({
        reportType: 'staffing_utilization',
        title: `Staffing Utilization — ${period.label}`,
        generatedBy: null,
        generatedByName: 'Current User',
        periodStart: period.start,
        periodEnd: period.end,
        filters: { period: period.label },
        rowCount: weeklyData.length,
        fileName,
        status: 'completed',
        notes: '',
      });
      if (logged) onExportLogged(logged);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5 pt-5">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {PERIOD_OPTIONS.map((p, i) => (
            <button
              key={p.label}
              onClick={() => setPeriodIdx(i)}
              className={`px-3 py-1.5 text-xs font-500 rounded-lg border transition-colors ${
                periodIdx === i
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-500 border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button onClick={handleExport} disabled={exporting || rows.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50">
            <Download size={13} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Hours', value: totalHours.toFixed(0) + ' hrs' },
          { label: 'OT Rate', value: otPct + '%', highlight: parseFloat(otPct) > 15 },
          { label: 'Unique Staff', value: uniqueStaff.toString() },
          { label: 'Avg Hrs / Staff', value: avgHoursPerStaff + ' hrs' },
        ].map((c) => (
          <div key={c.label} className="card-elevated p-4">
            <p className="text-xs text-muted-foreground font-500 mb-1">{c.label}</p>
            <p className={`text-xl font-700 ${c.highlight ? 'text-warning' : 'text-foreground'}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {error && <div className="bg-danger/10 border border-danger/20 rounded-lg px-4 py-3 text-sm text-danger">{error}</div>}

      {/* Weekly trend chart */}
      <div className="card-elevated p-5">
        <h3 className="text-sm font-600 text-foreground mb-4">Weekly Hours Trend</h3>
        {loading ? (
          <div className="h-52 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : weeklyData.length === 0 ? (
          <div className="h-52 flex items-center justify-center text-sm text-muted-foreground">No data for this period</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={weeklyData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="otGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--warning)" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="var(--warning)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
              <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="totalHours" name="Total Hrs" stroke="var(--primary)" fill="url(#totalGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="otHours" name="OT Hrs" stroke="var(--warning)" fill="url(#otGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Facility breakdown */}
      <div className="card-elevated p-5">
        <h3 className="text-sm font-600 text-foreground mb-4">Hours by Facility</h3>
        {loading ? (
          <div className="h-44 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : facilityData.length === 0 ? (
          <div className="h-44 flex items-center justify-center text-sm text-muted-foreground">No facility data</div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={facilityData} layout="vertical" margin={{ top: 4, right: 16, left: 80, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
              <YAxis type="category" dataKey="facility" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} width={76} />
              <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="totalHours" name="Total Hrs" fill="var(--primary)" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
